import React, { useMemo, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { PetDefault, UserPet } from '@typings/pets/pet.interface';
import { PetPersonalityTypeMap } from '../components/PetPersonality.ts/PetPersonalityType';
import { PetCharacter } from '.';
import { useDocument } from '@nandorojo/swr-firestore';
import { usePetLabel } from '../hooks/usePetLabel';

// 펫 성격 설정 문서 타입
type PersonalitiesDoc = {
  types: Record<string, string>;
  scripts: Record<string, string[]>;
  updatedAt?: any;
};

type Props = {
  pet?: UserPet | null;
  containerBounds?: DOMRect | null;
  isFloating?: boolean;
  onPetClick?: () => void;
};

const PetDisplay: React.FC<Props> = ({
  pet,
  isFloating,
  onPetClick,
}) => {
  // PetArea 자체 bounds 측정
  const petAreaRef = useRef<HTMLDivElement>(null);
  const [petAreaBounds, setPetAreaBounds] = useState<DOMRect | undefined>();

  useEffect(() => {
    const el = petAreaRef.current;
    if (!el) return;
    const update = () => setPetAreaBounds(el.getBoundingClientRect());
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, []);

  // 기본 설정 배경 + 커스텀 용어
  const { petLabel, petDefaultSetting } = usePetLabel();
  const petDefaultSettingDoc = petDefaultSetting as PetDefault | undefined;

  // 커스텀 성격 설정 가져오기
  const { data: personalitiesData } = useDocument<PersonalitiesDoc>(
    'petSetting/personalities',
    { listen: true },
  );

  // 기본값 + 커스텀 성격 타입 병합
  const mergedPersonalityTypes = useMemo(() => {
    const defaultTypes = { ...PetPersonalityTypeMap } as Record<string, string>;
    const customTypes = personalitiesData?.types || {};
    return { ...defaultTypes, ...customTypes };
  }, [personalitiesData]);
  const userBgURL = pet?.backgroundImgURL;
  const defaultBgURL = petDefaultSettingDoc?.backgroundImgURL;

  // 최종 배경: 유저 지정 > 기본 설정 > 스타일 기본 그라디언트
  const finalBgStyle = useMemo(() => {
    if (userBgURL) {
      return `center / cover no-repeat url(${userBgURL}) var(--backgroundColorSolid)`;
    }
    if (defaultBgURL) {
      return `center / cover no-repeat url(${defaultBgURL}) var(--backgroundColorSolid)`;
    }
    return '';
  }, [userBgURL, defaultBgURL]);

  return (
    <>
      <PetArea ref={petAreaRef} style={finalBgStyle ? { background: finalBgStyle } : {}}>
        {!isFloating ? (
          <PetCharacter
            pet={pet as any}
            containerBounds={petAreaBounds}
            onClick={onPetClick}
          />
        ) : (
          <AwayPlaceholder>
            <AwayBox>{petLabel}이(가) 외출 중입니다</AwayBox>
          </AwayPlaceholder>
        )}
      </PetArea>

      <PetInfoCard>
        <PetInfo>
          <PetLevel>Lv. {Number((pet as any)?.level || 1)}</PetLevel>
          <PetName>
            {(pet as any)?.customName || (pet as any)?.petTemplateId}
          </PetName>
        </PetInfo>
        <PetPersonality>
          {((pet as any)?.personalities || []).map((pKey: string) => (
            <div key={pKey}>
              #{mergedPersonalityTypes[pKey] || pKey}
            </div>
          ))}
        </PetPersonality>
      </PetInfoCard>
    </>
  );
};

export default PetDisplay;

const PetArea = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  background: center center / cover no-repeat
    linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
`;

const PetInfoCard = styled.div`
  position: absolute;
  right: 0;
  bottom: -40px;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 4;
  width: max-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  justify-content: center;
  background: var(--cardBackgroundColor);
  border: 1px solid var(--cardLineColor);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 8px 16px;
`;

const PetInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const PetName = styled.h2`
  font-size: 17px;
  font-weight: bold;
  color: var(--textColor);
  text-align: center;
`;

const PetLevel = styled.div`
  background: var(--primaryColor);
  color: var(--subColor);
  font-weight: bold;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
`;

const PetPersonality = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
  justify-content: center;
  color: var(--subTextColor);
`;

const AwayPlaceholder = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AwayBox = styled.div`
  background: var(--cardBackgroundColor);
  border: 1px solid var(--cardLineColor);
  border-radius: 12px;
  padding: 12px 16px;
  color: var(--subTextColor);
  font-size: 13px;
  backdrop-filter: blur(8px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
`;
